import { useId, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface TabItem<TValue extends string> {
  value: TValue;
  label: ReactNode;
  badge?: ReactNode;
}

interface TabsProps<TValue extends string> {
  value: TValue;
  onChange: (value: TValue) => void;
  items: ReadonlyArray<TabItem<TValue>>;
  ariaLabel?: string;
  className?: string;
}

/**
 * Keyboard-accessible tab list (ArrowLeft/Right + Home/End move focus).
 * Pure-controlled: parent owns the active tab so deep links can drive it.
 */
export function Tabs<TValue extends string>({
  value,
  onChange,
  items,
  ariaLabel,
  className,
}: TabsProps<TValue>) {
  const idBase = useId();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  function focusTab(next: number) {
    const target = items[(next + items.length) % items.length];
    if (!target) return;
    tabRefs.current[target.value]?.focus();
    onChange(target.value);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, idx: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusTab(idx + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusTab(idx - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusTab(0);
        break;
      case 'End':
        e.preventDefault();
        focusTab(items.length - 1);
        break;
    }
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex flex-wrap items-center gap-1 border-b border-border', className)}
    >
      {items.map((item, idx) => {
        const active = item.value === value;
        const id = `${idBase}-${item.value}`;
        return (
          <button
            key={item.value}
            ref={(el) => {
              tabRefs.current[item.value] = el;
            }}
            type="button"
            role="tab"
            id={id}
            aria-selected={active}
            aria-controls={`${id}-panel`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={(e) => onKeyDown(e, idx)}
            className={cn(
              '-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              active
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <span>{item.label}</span>
            {item.badge && <span>{item.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}

interface TabPanelProps {
  active: boolean;
  children: ReactNode;
  id?: string;
  className?: string;
}

export function TabPanel({ active, children, id, className }: TabPanelProps) {
  if (!active) return null;
  return (
    <div
      role="tabpanel"
      id={id ? `${id}-panel` : undefined}
      aria-labelledby={id}
      className={cn('pt-6', className)}
    >
      {children}
    </div>
  );
}
